import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { dailyLogsApi, type Site } from '@/api/endpoints';
import { Alert, EmptyState, FilterBar, ListGroup, Spinner } from '@/components/ui';
import { DayLabel, LogDetail, LogListRow, groupByDay } from '@/features/daily-logs/LogList';
import { PendingCard, usePendingItems } from '@/features/upload-queue';
import { useErrorMessage } from '@/hooks/useErrorMessage';

/** 服务端 `take: 200`。取满了要说出来，不能让更早的日报静默消失 */
const MY_LOGS_CAP = 200;

/**
 * 我在这个工地写过的日报（CR-023 新增，CR-042 收进日报页的「我的」tab，CR-082 重做）。
 *
 * 和「本工地」的 `LogList` 同一种读法：按日期分组的列表，点一条打开详情（`LogDetail` 共用）。
 * 不共用列表组件：这边是「我自己的、不分页」，那边是「全体的、带分页」。
 *
 * **区间由页面给**（CR-048，客户原话「我的tab里复用 本工地tab的筛选」）：切 tab 区间不变。
 * 筛选在服务端做（`me/daily-logs?siteId=&from=&to=`）—— 改之前取最近 50 条回前端再按日期过滤，
 * 选「上个月」时更早的日报会静默消失。
 */
export function MyLogsList({
  site,
  from,
  to,
  toolbar,
}: {
  site: Site;
  from: string;
  to: string;
  /** 工具条左边：本工地 / 我的、日期区间 */
  toolbar?: ReactNode;
}) {
  const { t } = useTranslation();
  const toMessage = useErrorMessage();
  const [openId, setOpenId] = useState<string | null>(null);

  const logs = useQuery({
    queryKey: ['my-logs', site.id, from, to],
    queryFn: () => dailyLogsApi.myLogs(site.id, { from, to }),
  });

  // 还在队列里的日报（CR-037）：服务端还没有它们，摆在最上面标「上传中」
  const pending = usePendingItems('DAILY_LOG', site.id);

  const list = logs.data ?? [];
  const capped = list.length >= MY_LOGS_CAP;
  const opened = list.find((log) => log.id === openId) ?? null;

  return (
    <>
      <FilterBar summary={list.length > 0 && t('logs.totalCount', { count: list.length })}>{toolbar}</FilterBar>

      {logs.isLoading && (
        <div className="flex justify-center py-8 text-ink-3">
          <Spinner />
        </div>
      )}
      {logs.isError && <Alert>{toMessage(logs.error)}</Alert>}

      {!logs.isLoading && list.length === 0 && pending.length === 0 && (
        <EmptyState title={t('logs.empty')} hint={t('logs.emptyHintWorker')} />
      )}

      {pending.length > 0 && (
        <ul className="mb-3 flex flex-col gap-2">
          {pending.map((item) => (
            <PendingCard
              key={item.id}
              item={item}
              title={String(item.payload.content ?? t('queue.kindDailyLog'))}
              subtitle={String(item.payload.workDate ?? '').slice(0, 10)}
            />
          ))}
        </ul>
      )}

      {capped && <p className="mb-2 text-caption text-ink-3">{t('logs.cappedHint', { count: MY_LOGS_CAP })}</p>}

      <div className="flex flex-col gap-4">
        {groupByDay(list).map(([day, logsOfDay]) => (
          <ListGroup key={day} title={<DayLabel workDate={logsOfDay[0]!.workDate} />}>
            {logsOfDay.map((log) => (
              <LogListRow key={log.id} log={log} timezone={site.timezone} onOpen={() => setOpenId(log.id)} />
            ))}
          </ListGroup>
        ))}
      </div>

      <LogDetail log={opened} timezone={site.timezone} onClose={() => setOpenId(null)} />
    </>
  );
}
